import React from 'react';
import { motion } from 'framer-motion';
import { 
  ClockIcon,
  DocumentTextIcon,
  PhotoIcon,
  CheckCircleIcon,
  ChatBubbleLeftIcon,
  CurrencyDollarIcon,
  UserPlusIcon,
  CalendarIcon,
  ArrowTrendingUpIcon
} from '@heroicons/react/24/outline';
import { VisibilityBadge } from './VisibilityIndicator';

const TeamActivityFeed = ({ 
  activities = [], 
  showFilters = true,
  privacyMode = 'team',
  currentUserId 
}) => {
  const activityIcons = {
    invoice: CurrencyDollarIcon,
    project: DocumentTextIcon,
    photo: PhotoIcon,
    task: CheckCircleIcon,
    comment: ChatBubbleLeftIcon,
    team: UserPlusIcon,
    event: CalendarIcon,
    metric: ArrowTrendingUpIcon
  };

  const activityColors = {
    invoice: 'from-green-500 to-green-600',
    project: 'from-brand to-[#FF9D5C]',
    photo: 'from-purple-500 to-purple-600',
    task: 'from-blue-500 to-blue-600',
    comment: 'from-[#E6A15E] to-brand',
    team: 'from-indigo-500 to-indigo-600',
    event: 'from-pink-500 to-pink-600',
    metric: 'from-yellow-500 to-yellow-600'
  };

  // Filter activities based on privacy mode
  const filteredActivities = activities.filter(activity => {
    if (privacyMode === 'personal') {
      return activity.userId === currentUserId || activity.visibility === 'private';
    }
    if (privacyMode === 'focus') {
      return activity.userId === currentUserId;
    }
    return activity.visibility !== 'private' || activity.userId === currentUserId;
  });

  const formatTime = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diff = Math.floor((now - time) / 1000); // seconds

    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return time.toLocaleDateString();
  };

  return (
    <div className="bg-white dark:bg-[#111111] border border-gray-200 dark:border-[#2A2A2A] rounded-xl">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-[#2A2A2A]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-gradient-to-br from-[#E6A15E] to-brand">
              <ClockIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Team Activity
              </h3>
              <p className="text-xs text-gray-500 dark:text-[#666666]">
                {filteredActivities.length} recent activities
              </p>
            </div>
          </div>

          {showFilters && (
            <div className="flex items-center gap-2">
              <button className="px-3 py-1 text-xs font-medium text-gray-600 dark:text-[#999999] hover:text-brand transition-colors">
                All
              </button>
              <button className="px-3 py-1 text-xs font-medium text-gray-600 dark:text-[#999999] hover:text-brand transition-colors">
                Projects
              </button>
              <button className="px-3 py-1 text-xs font-medium text-gray-600 dark:text-[#999999] hover:text-brand transition-colors">
                Tasks
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Activity List */}
      <div className="max-h-96 overflow-y-auto custom-scrollbar">
        {filteredActivities.length > 0 ? (
          filteredActivities.map((activity, index) => {
            const Icon = activityIcons[activity.type] || DocumentTextIcon;
            const colorClass = activityColors[activity.type] || 'from-gray-500 to-gray-600';

            return (
              <motion.div
                key={activity.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className="px-6 py-4 border-b border-gray-100 dark:border-[#1A1A1A] hover:bg-gray-50 dark:hover:bg-[#0F0F0F] transition-colors group"
              >
                <div className="flex items-start gap-3">
                  {/* Activity Icon */}
                  <div className={`p-2 rounded-lg bg-gradient-to-br ${colorClass} flex-shrink-0`}>
                    <Icon className="w-4 h-4 text-white" />
                  </div>

                  {/* Activity Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {activity.user?.name || 'Someone'}
                      </span>
                      {activity.visibility && (
                        <VisibilityBadge visibility={activity.visibility} />
                      )}
                      <span className="text-xs text-gray-500 dark:text-[#666666]">
                        {formatTime(activity.timestamp)}
                      </span>
                    </div>
                    
                    <p className="text-sm text-gray-700 dark:text-[#CCCCCC]">
                      {activity.action}
                      {activity.target && (
                        <span className="font-medium text-brand hover:underline cursor-pointer">
                          {' '}{activity.target}
                        </span>
                      )}
                    </p>

                    {/* Additional Context */}
                    {activity.details && (
                      <div className="mt-2 px-3 py-2 bg-gray-50 dark:bg-[#0F0F0F] rounded-lg">
                        <p className="text-xs text-gray-600 dark:text-[#999999]">
                          {activity.details}
                        </p>
                      </div>
                    )}

                    {/* Quick Actions */}
                    <div className="mt-2 flex items-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button className="text-xs text-gray-500 hover:text-brand transition-colors">
                        View
                      </button>
                      <button className="text-xs text-gray-500 hover:text-brand transition-colors">
                        Comment
                      </button>
                    </div>
                  </div>

                  {/* Activity Thumbnail */}
                  {activity.thumbnail && (
                    <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                      <img 
                        src={activity.thumbnail} 
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })
        ) : (
          <div className="px-6 py-12 text-center">
            <ClockIcon className="w-12 h-12 mx-auto text-gray-300 dark:text-[#2A2A2A] mb-3" />
            <p className="text-sm text-gray-500 dark:text-[#666666]">
              No recent activity to show
            </p>
            <p className="text-xs text-gray-400 dark:text-[#444444] mt-1">
              {privacyMode === 'personal' 
                ? "You're viewing your personal activity only"
                : privacyMode === 'focus'
                ? "Focus mode - showing minimal activity"
                : "Activities will appear here as your team works"}
            </p>
          </div>
        )}
      </div>

      {/* View All Link */}
      {filteredActivities.length > 5 && (
        <div className="px-6 py-3 border-t border-gray-200 dark:border-[#2A2A2A]">
          <button className="text-sm font-medium text-brand hover:text-brand-hover transition-colors">
            View all activity →
          </button>
        </div>
      )}
    </div>
  );
};

export default TeamActivityFeed;